import React, { useState, useMemo } from "react";
import {
    Paper, Typography, Grid, TextField, Button, Box, Alert, Table, TableHead, TableRow, TableCell, TableBody,
} from "@mui/material";
import PrintPanel from "./PrintPanel";
import { penaltyPdf, fmtPts } from "./racePdf";
import { penaltySupport, validateHeader } from "./raceTypes";

// Export Swiss-Ski : calcul points/pénalité Swiss-Ski (÷ 3.75, W/M séparés)
// et XML Integral v1.9. Logique reprise de TimingSwissSkiExport.jsx.
export default function SwissSkiPanel({ config, setConfig, eventKey, apiFetch, saveConfig, setError, setStatus }) {
    const [calc, setCalc] = useState(null);
    const support = penaltySupport(config);
    const { isValid, errors } = validateHeader(config);
    const set = (f, v) => setConfig((p) => ({ ...p, [f]: v }));

    const runCalc = async () => {
        setError("");
        if (!(await saveConfig())) return;
        const res = await apiFetch(`/api/timing/swissski-calc/${encodeURIComponent(eventKey)}`);
        if (!res) return;
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            setError(data.error || `HTTP ${res.status}`);
            return;
        }
        const data = await res.json();
        setCalc(data);
        setConfig((p) => ({
            ...p,
            appPenaltyW: p.appPenaltyW || (data.calcPenaltyW != null ? data.calcPenaltyW.toFixed(2) : ""),
            appPenaltyM: p.appPenaltyM || (data.calcPenaltyM != null ? data.calcPenaltyM.toFixed(2) : ""),
        }));
    };

    const penW = parseFloat(config.appPenaltyW) || calc?.calcPenaltyW || 0;
    const penM = parseFloat(config.appPenaltyM) || calc?.calcPenaltyM || 0;

    const pointsByBib = useMemo(() => {
        if (!calc) return null;
        const out = {};
        calc.racePoints.forEach((r) => {
            if (r.points == null) return;
            const pen = (r.gender || "").toUpperCase().startsWith("W") ? penW : penM;
            out[String(r.bib)] = Math.max(0, r.points + pen);
        });
        return out;
    }, [calc, penW, penM]);

    const downloadXml = async () => {
        setError("");
        if (!(await saveConfig())) return;
        const res = await apiFetch(`/api/timing/swissski-export/${encodeURIComponent(eventKey)}`, { method: "POST" });
        if (!res) return;
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            setError(data.error || `HTTP ${res.status}`);
            return;
        }
        const url = URL.createObjectURL(await res.blob());
        const a = document.createElement("a");
        a.href = url;
        a.download = `${config.codex || eventKey}.xml`;
        a.click();
        URL.revokeObjectURL(url);
        setStatus("XML Integral téléchargé");
    };

    const printPenalty = () => {
        if (!calc) {
            setError("Calculez d'abord les points pour imprimer le détail de la pénalité.");
            return;
        }
        const tables = [
            { label: "Femmes — 5 meilleurs temps", detail: calc.penaltyDetailW },
            { label: "Hommes — 5 meilleurs temps", detail: calc.penaltyDetailM },
        ].filter((t) => t.detail).map(({ label, detail }) => ({
            label,
            head: ["Rang", "Nom", "Points", "Sélection"],
            body: detail.rows.map((r, i) => [`${i + 1}.`, r.name, fmtPts(r.rawPoints), fmtPts(r.selected)]),
            foot: [["", "", "Somme", fmtPts(detail.sum)], ["", "", "Pénalité (÷ 3.75)", fmtPts(detail.penalty)]],
            struckRows: new Set(detail.rows.map((r, i) => (r.struck ? i : -1)).filter((i) => i >= 0)),
            // Barré : nom et points des résultats non retenus (pas le rang).
            struckColumns: [1, 2],
        }));
        penaltyPdf({
            config, eventKey, title: "Calcul de la pénalité de course (Swiss-Ski)",
            infoLines: [`Liste de points : ${config.plist || "—"}`, `Pénalité appliquée : F ${fmtPts(penW)} / H ${fmtPts(penM)}`],
            tables,
        });
    };

    return (
        <>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" mb={2}>Points Swiss-Ski</Typography>

                {!support.supported ? (
                    <Alert severity="info">{support.reason}</Alert>
                ) : (
                    <>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField label="Liste de points (PList)" size="small" fullWidth
                                           value={config.plist || ""} onChange={(e) => set("plist", e.target.value)} />
                            </Grid>
                            {[["weather", "Météo (XML)"], ["snow", "Neige (XML)"], ["temperature", "Température (XML)"]].map(([f, l]) => (
                                <Grid item xs={12} sm={4} md={2.66} key={f}>
                                    <TextField label={l} size="small" fullWidth value={config[f] || ""} onChange={(e) => set(f, e.target.value)} />
                                </Grid>
                            ))}
                        </Grid>
                        <Button variant="contained" sx={{ mt: 2 }} onClick={runCalc}>Calculer points et pénalité</Button>

                        {calc && (
                            <Box sx={{ mt: 3 }}>
                                <Grid container spacing={3}>
                                    {[
                                        { label: "Femmes", calcVal: calc.calcPenaltyW, key: "appPenaltyW", detail: calc.penaltyDetailW },
                                        { label: "Hommes", calcVal: calc.calcPenaltyM, key: "appPenaltyM", detail: calc.penaltyDetailM },
                                    ].map((g) => (
                                        <Grid item xs={12} md={6} key={g.key}>
                                            <Typography variant="subtitle2">{g.label}</Typography>
                                            <Typography variant="body2">Pénalité calculée : <b>{g.calcVal != null ? g.calcVal.toFixed(2) : "—"}</b></Typography>
                                            <TextField label="Pénalité appliquée" size="small" sx={{ mt: 1, width: 180 }}
                                                       value={config[g.key] || ""} onChange={(e) => set(g.key, e.target.value)} />
                                            {g.detail && (
                                                <Table size="small" sx={{ mt: 1 }}>
                                                    <TableHead>
                                                        <TableRow><TableCell>Nom</TableCell><TableCell align="right">Points</TableCell><TableCell align="right">Sélection</TableCell></TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {g.detail.rows.map((r, i) => (
                                                            <TableRow key={i} sx={r.struck ? { "& td:nth-of-type(-n+2)": { textDecoration: "line-through", textDecorationThickness: "2px" } } : undefined}>
                                                                <TableCell>{r.name}</TableCell>
                                                                <TableCell align="right">{fmtPts(r.rawPoints)}</TableCell>
                                                                <TableCell align="right">{fmtPts(r.selected)}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            )}
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        )}
                    </>
                )}
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" mb={1}>Export XML Integral (Swiss-Ski)</Typography>
                {!isValid && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Raceheader incomplet : {Object.entries(errors).map(([f, m]) => `${f} (${m})`).join(", ")}
                    </Alert>
                )}
                <Button variant="contained" color="success" disabled={!isValid} onClick={downloadXml}>
                    Télécharger le XML
                </Button>
            </Paper>

            <PrintPanel
                config={config} eventKey={eventKey} apiFetch={apiFetch} setError={setError}
                pointsByBib={support.supported ? pointsByBib : null}
                pointsMissing={support.supported && !calc ? "Calculez les points pour imprimer les résultats de l'épreuve." : ""}
                infoLines={calc ? [`Points list: ${config.plist || "—"}   Applied penalty: Women ${fmtPts(penW)} / Men ${fmtPts(penM)}`] : []}
                onPenaltyPdf={support.supported ? printPenalty : undefined}
            />
        </>
    );
}